import { CustomerTags } from '@/modules/customer/domain/Customer';
import getCustomerAddress from '@/modules/perseo-billing/hooks/order-confirmation/get-customer-data';
import getShippingBasketSummary from '@/modules/perseo-billing/hooks/order-confirmation/get-shipping-basket-summary';
import getTotalSummary from '@/modules/perseo-billing/hooks/order-confirmation/get-total-summary';
import type { IOrderConfirmation } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import { EPayStatus } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import { type IOrder } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import type { IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

enum EAuthType {
  REGISTERED = 'registered',
  GUEST = 'guest'
}

export interface IUserCustomerData {
  firstName: string;
  lastName: string;
  email: string;
  rut: string;
  isColabCustomer: boolean;
  phone: string;
  documentType?: IdentificationDocumentType;
  documentID?: string;
}

export const useGetCustomerData = () => {
  const { customer } = useCustomerContext();
  const { data: customerData } = customer ?? {};
  const defaultPhone =
    customerData?.addresses?.find((address) => address.isDefault)?.phone ?? '';

  const phone = customerData?.phone ? customerData?.phone : defaultPhone;

  const {
    rut = '',
    email = '',
    customerTags = [],
    firstName = '',
    lastName = '',
    documentType,
    documentID
  } = customerData ?? {};

  const isColabCustomer = customerTags.includes(CustomerTags.CENCOSUD_EMPLOYEE);

  return {
    firstName: firstName,
    lastName: lastName,
    email: email,
    rut: rut,
    isColabCustomer,
    phone: phone,
    documentType,
    documentID
  };
};

const hasCencoPayPaymentMethod = (order: IOrder): boolean => {
  return order?.payments?.some((payment) => {
    return payment.paymentMethodInfo.method === 'CENCOPAY';
  });
};

const useMapOrderToConfirmation = () => {
  const customer = useGetCustomerData();

  const transformData = (order: IOrder): IOrderConfirmation => {
    if (!order) return {} as IOrderConfirmation;

    const authType = order?.customerId ? EAuthType.REGISTERED : EAuthType.GUEST;

    const isCencoPay = hasCencoPayPaymentMethod(order);

    const isFastCheckout: boolean = !!(
      order?.customCartType === 'fast-checkout'
    );
    return {
      id: order?.id ?? '',
      order: order?.orderNumber ?? '',
      authType: authType,
      isCencoPay: isCencoPay ?? false,
      customer: getCustomerAddress(order, customer),
      basketSummary: getShippingBasketSummary(order, customer),
      cencoPayStatus: EPayStatus.PENDING,
      totalSummary: getTotalSummary(order),
      tokenUserId: order?.tokenUserId,
      isFastCheckout,
      customerId: order?.customerId,
      anonymousId: order?.anonymousId
    };
  };

  return { transformData };
};

export { useMapOrderToConfirmation };
